import { useState, useCallback } from 'react';
import gamesService, { Game, CreateGameRequest, UpdateGameRequest } from '../services/gamesService';

export const useGamesManagement = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load all games
  const loadGames = useCallback(async (stateId?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await gamesService.getGames(stateId);
      
      if (response.success && response.data) {
        setGames(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.error || 'Failed to load games');
      }
    } catch (err) {
      console.error('Error loading games:', err);
      // Error toast is now handled by the service automatically
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new game
  const createGame = useCallback(async (gameData: CreateGameRequest): Promise<boolean> => {
    try {
      setCreateLoading(true);
      setError(null);
      const response = await gamesService.createGame(gameData);
      
      if (response.success && response.data) {
        const newGame = Array.isArray(response.data) ? response.data[0] : response.data;
        setGames(prev => [newGame, ...prev]);
        return true;
      } else {
        setError(response.error || 'Failed to create game');
        return false;
      }
    } catch (err) {
      console.error('Error creating game:', err);
      // Error toast is now handled by the service automatically
      setError('Failed to create game');
      return false;
    } finally {
      setCreateLoading(false);
    }
  }, []);

  // Update game
  const updateGame = useCallback(async (id: string, gameData: UpdateGameRequest): Promise<boolean> => {
    try {
      setUpdateLoading(true);
      setError(null);
      const response = await gamesService.updateGame(id, gameData);
      
      if (response.success && response.data) {
        const updatedGame = Array.isArray(response.data) ? response.data[0] : response.data;
        setGames(prev => prev.map(game => game.id === id ? updatedGame : game));
        return true;
      } else {
        setError(response.error || 'Failed to update game');
        return false;
      }
    } catch (err) {
      console.error('Error updating game:', err);
      // Error toast is now handled by the service automatically
      setError('Failed to update game');
      return false;
    } finally {
      setUpdateLoading(false);
    }
  }, []);

  // Delete game
  const deleteGame = useCallback(async (id: string): Promise<boolean> => {
    try {
      setDeleteLoading(true);
      setError(null);
      const response = await gamesService.deleteGame(id);
      
      if (response.success) {
        setGames(prev => prev.filter(game => game.id !== id));
        return true;
      } else {
        setError(response.error || 'Failed to delete game');
        return false;
      }
    } catch (err) {
      console.error('Error deleting game:', err);
      // Error toast is now handled by the service automatically
      setError('Failed to delete game');
      return false;
    } finally {
      setDeleteLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    games,
    loading,
    error,
    createLoading,
    updateLoading,
    deleteLoading,
    loadGames,
    createGame,
    updateGame,
    deleteGame,
    clearError,
  };
}; 